/* ============================================================
   pj-motion.js — editorial motion driver for pj-editorial-hero
   and pj-chapter. Dependency-free IIFE, loaded with `defer` from
   the sections themselves (idempotent when both sections emit
   the tag: the window.PJMotion guard makes the second copy inert).

   Contract with pj-editorial.css / the sections:
   - sets --pj-chrome-h on :root  (header + announcement height)
   - [data-pj-kenburns]           gets --pj-kb-state running|paused
   - [data-pj-parallax="N"]       translated inside its parent
   - [data-pj-rotator]            cycles [data-pj-frame] .is-active
   - [data-pj-hero-video]         plays in-view; .is-playing on hero
   - [data-pj-defer]              frames whose src/srcset attach lazily

   Reduced motion: every motion feature bails entirely (and tears
   down live if the media query flips). The chrome measurement
   still runs — it is layout truth, not motion.
   ============================================================ */
(function () {
  'use strict';
  if (window.PJMotion) return;

  var docEl = document.documentElement;
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqDesktop = window.matchMedia('(min-width: 750px)');
  var teardowns = [];

  function onMq(mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }

  /* ----------------------------------------------------------
     CHROME HEIGHT → --pj-chrome-h (DOMContentLoaded + resize)
     ---------------------------------------------------------- */
  var chromeTimer = null;

  function measureChrome() {
    var h = 0;
    var header = document.querySelector('.section-header');
    var bar = document.querySelector('.announcement-bar-section');
    if (header) h += header.offsetHeight;
    if (bar) h += bar.offsetHeight;
    if (h > 0) docEl.style.setProperty('--pj-chrome-h', Math.round(h) + 'px');
  }

  function queueMeasure() {
    clearTimeout(chromeTimer);
    chromeTimer = setTimeout(measureChrome, 150);
  }

  /* ----------------------------------------------------------
     PARALLAX — single shared rAF + scroll-dirty flag; measures
     the overflow-hidden HOST (not the translated element, which
     would feed back into itself). Desktop only (>= 750px).
     ---------------------------------------------------------- */
  var par = { items: [], visible: 0, rafId: null, io: null, bound: false };

  function parTick() {
    par.rafId = null;
    var vc = window.innerHeight / 2;
    var desktop = mqDesktop.matches;
    for (var i = 0; i < par.items.length; i++) {
      var it = par.items[i];
      if (!it.inView || !desktop) {
        if (it.applied) {
          it.el.style.transform = '';
          it.applied = false;
        }
        continue;
      }
      var rect = it.host.getBoundingClientRect();
      var y = ((vc - (rect.top + rect.height / 2)) * it.strength) / 100;
      it.el.style.transform = 'translate3d(0, ' + y.toFixed(1) + 'px, 0)';
      it.applied = true;
    }
  }

  function parQueue() {
    if (par.visible > 0 && par.rafId === null) par.rafId = requestAnimationFrame(parTick);
  }

  function parAdd(el) {
    if (el.dataset.pjParInit) return;
    el.dataset.pjParInit = '1';
    var item = {
      el: el,
      host: el.parentElement || el,
      strength: parseFloat(el.getAttribute('data-pj-parallax')) || 15,
      inView: false,
      applied: false
    };
    item.host.__pjPar = item;
    par.items.push(item);
    if (!par.io) {
      par.io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var it = entries[i].target.__pjPar;
            if (!it) continue;
            if (entries[i].isIntersecting && !it.inView) {
              it.inView = true;
              par.visible++;
            } else if (!entries[i].isIntersecting && it.inView) {
              it.inView = false;
              par.visible--;
            }
          }
          parQueue();
        },
        { rootMargin: '24% 0px' }
      );
    }
    par.io.observe(item.host);
    if (!par.bound) {
      par.bound = true;
      window.addEventListener('scroll', parQueue, { passive: true });
      window.addEventListener('resize', parQueue, { passive: true });
    }
    parQueue();
  }

  function parTeardown() {
    if (par.io) {
      par.io.disconnect();
      par.io = null;
    }
    if (par.bound) {
      window.removeEventListener('scroll', parQueue);
      window.removeEventListener('resize', parQueue);
      par.bound = false;
    }
    if (par.rafId !== null) {
      cancelAnimationFrame(par.rafId);
      par.rafId = null;
    }
    for (var i = 0; i < par.items.length; i++) {
      var it = par.items[i];
      it.el.style.transform = '';
      delete it.el.dataset.pjParInit;
      if (it.host.__pjPar) delete it.host.__pjPar;
    }
    par.items = [];
    par.visible = 0;
  }

  /* ----------------------------------------------------------
     KEN BURNS — pause the CSS animation off-screen
     ---------------------------------------------------------- */
  var kbIo = null;

  function kbInit(root) {
    var els = root.querySelectorAll('[data-pj-kenburns]');
    if (!els.length) return;
    if (!kbIo) {
      kbIo = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            entries[i].target.style.setProperty(
              '--pj-kb-state',
              entries[i].isIntersecting ? 'running' : 'paused'
            );
          }
        },
        { threshold: 0.05 }
      );
    }
    for (var i = 0; i < els.length; i++) {
      if (els[i].dataset.pjKbInit) continue;
      els[i].dataset.pjKbInit = '1';
      kbIo.observe(els[i]);
    }
  }

  function kbTeardown() {
    if (kbIo) {
      kbIo.disconnect();
      kbIo = null;
    }
    var els = document.querySelectorAll('[data-pj-kenburns]');
    for (var i = 0; i < els.length; i++) {
      els[i].style.removeProperty('--pj-kb-state');
      delete els[i].dataset.pjKbInit;
    }
  }

  /* ----------------------------------------------------------
     CROSSFADE ROTATOR — starts after window load + one interval;
     lazily attaches deferred frame srcs before the first swap;
     pauses off-screen and on document.hidden; yields while the
     hero video is playing.
     ---------------------------------------------------------- */
  function rotInit(rotEl) {
    if (rotEl.dataset.pjRotInit) return;
    rotEl.dataset.pjRotInit = '1';
    var frames = rotEl.querySelectorAll('[data-pj-frame]');
    if (frames.length < 2) return;
    var interval = parseInt(rotEl.getAttribute('data-pj-interval'), 10) || 7000;
    var idx = 0;
    for (var f = 0; f < frames.length; f++) {
      if (frames[f].classList.contains('is-active')) {
        idx = f;
        break;
      }
    }
    var timer = null;
    var inView = true;
    var started = false;

    function attachDeferred() {
      var deferred = rotEl.querySelectorAll('[data-pj-defer]');
      for (var i = 0; i < deferred.length; i++) {
        var srcs = deferred[i].querySelectorAll('[data-srcset]');
        for (var j = 0; j < srcs.length; j++) {
          srcs[j].setAttribute('srcset', srcs[j].getAttribute('data-srcset'));
          srcs[j].removeAttribute('data-srcset');
        }
        var imgs = deferred[i].querySelectorAll('img[data-src]');
        for (var k = 0; k < imgs.length; k++) {
          imgs[k].setAttribute('src', imgs[k].getAttribute('data-src'));
          imgs[k].removeAttribute('data-src');
        }
        deferred[i].removeAttribute('data-pj-defer');
      }
    }

    function restartKb(frame) {
      var media = frame.querySelector('.pj-kb');
      if (!media) return;
      media.style.animation = 'none';
      void media.offsetWidth;
      media.style.animation = '';
    }

    function swap() {
      if (document.hidden || !inView) return;
      if (rotEl.classList.contains('is-playing')) return;
      var current = frames[idx];
      idx = (idx + 1) % frames.length;
      var next = frames[idx];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      restartKb(next);
    }

    function play() {
      if (started && timer === null) timer = setInterval(swap, interval);
    }

    function pause() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    }

    function begin() {
      if (started) return;
      started = true;
      attachDeferred();
      if (inView && !document.hidden) play();
    }

    var io = new IntersectionObserver(
      function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        if (inView) play();
        else pause();
      },
      { threshold: 0.15 }
    );
    io.observe(rotEl);

    function onVis() {
      if (document.hidden) pause();
      else if (inView) play();
    }
    document.addEventListener('visibilitychange', onVis);

    if (document.readyState === 'complete') begin();
    else window.addEventListener('load', begin, { once: true });

    teardowns.push(function () {
      pause();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('load', begin);
      delete rotEl.dataset.pjRotInit;
    });
  }

  /* ----------------------------------------------------------
     HERO VIDEO — enhancement only. Sources ship as data-src;
     attach + play on viewport entry, .is-playing on success
     (CSS crossfades the stills away), silent revert on pause
     or error. Skipped entirely under Save-Data.
     ---------------------------------------------------------- */
  function vidInit(video) {
    if (video.dataset.pjVidInit) return;
    video.dataset.pjVidInit = '1';
    if (navigator.connection && navigator.connection.saveData) return;
    var host = video.closest('.pj-edhero') || video.parentElement;
    var attached = false;

    function attach() {
      if (attached) return;
      attached = true;
      var srcs = video.querySelectorAll('source[data-src]');
      for (var i = 0; i < srcs.length; i++) {
        srcs[i].setAttribute('src', srcs[i].getAttribute('data-src'));
        srcs[i].removeAttribute('data-src');
      }
      if (srcs.length) video.load();
    }

    function markPlaying() {
      host.classList.add('is-playing');
    }

    function markStopped() {
      host.classList.remove('is-playing');
    }

    function tryPlay() {
      attach();
      video.muted = true;
      var p;
      try {
        p = video.play();
      } catch (e) {
        markStopped();
        return;
      }
      if (p && typeof p.then === 'function') p.then(markPlaying).catch(markStopped);
    }

    video.addEventListener('playing', markPlaying);
    video.addEventListener('pause', markStopped);
    video.addEventListener('error', markStopped, true);

    var io = new IntersectionObserver(
      function (entries) {
        var e = entries[entries.length - 1];
        if (e.isIntersecting) tryPlay();
        else if (!video.paused) video.pause();
      },
      { threshold: 0.2 }
    );
    io.observe(host);

    teardowns.push(function () {
      io.disconnect();
      video.removeEventListener('playing', markPlaying);
      video.removeEventListener('pause', markStopped);
      video.removeEventListener('error', markStopped, true);
      try {
        if (!video.paused) video.pause();
      } catch (e) {
        /* noop */
      }
      markStopped();
      delete video.dataset.pjVidInit;
    });
  }

  /* ----------------------------------------------------------
     ORCHESTRATION
     ---------------------------------------------------------- */
  function initMotion(root) {
    kbInit(root);
    var pars = root.querySelectorAll('[data-pj-parallax]');
    for (var i = 0; i < pars.length; i++) parAdd(pars[i]);
    var rots = root.querySelectorAll('[data-pj-rotator]');
    for (var j = 0; j < rots.length; j++) rotInit(rots[j]);
    var vids = root.querySelectorAll('[data-pj-hero-video]');
    for (var k = 0; k < vids.length; k++) vidInit(vids[k]);
  }

  function teardownAll() {
    for (var i = 0; i < teardowns.length; i++) {
      try {
        teardowns[i]();
      } catch (e) {
        /* noop */
      }
    }
    teardowns = [];
    parTeardown();
    kbTeardown();
  }

  function init(root) {
    measureChrome();
    if (mqReduce.matches) return;
    initMotion(root || document);
  }

  onMq(mqReduce, function () {
    if (mqReduce.matches) teardownAll();
    else init(document);
  });

  window.addEventListener('resize', queueMeasure, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor: re-init freshly (re)rendered sections. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  window.PJMotion = { init: init };
})();
